<template>
  <div class="form-component" v-if="options && options.length">
    <label :for="controlId" class="animated hasContent" v-if="label">
      <span class="fixed-label">{{ label }}</span>
      <span class="animated-label">{{ label }}</span>
    </label>
    <ul class="option-group radio-group" v-bind:class="{ conditional: isConditional }"
      v-if="visibleOptions && visibleOptions.length > 0">
      <li class="option" v-for="(option, oIndex) in visibleOptions" :key="oIndex" :tabindex="option.disabled ? -1 : 0"
        :class="[option.disabled ? '-disabled' : '']"
        :style="option.disabled ? 'pointer-events: none' : 'cursor: pointer; pointer-events: all;'"
        @keydown.enter.prevent="optionSelected(option)" @keydown.space.prevent="optionSelected(option)"
        @click="optionSelected(option)">
        <slot name="option-name" v-bind:optionValue="option">
          {{ option.name ? option.name : option }}
        </slot>
        <div class="radio-tile" :class="{ checked: formValue === option.value }">
          <input v-if="isCheckboxes" type="checkbox" class="radio-button attribute-field" :id="controlId"
            :data-productattrid="id" :name="controlId" :value="option.value" :true-value="option.value" tabindex="-1"
            v-on:change="valueChanged($event)" v-model="formValue" />
          <input v-else type="radio" class="radio-button attribute-field" :name="controlId" :value="option.value"
            tabindex="-1" v-on:change="valueChanged($event)" v-model="formValue" :id="controlId"
            :disabled="option.disabled" />
        </div>
      </li>
    </ul>
  </div>
</template>
<script>
const valueChangedEvent = 'value-changed'
export default {
  name: 'form-radio-buttons',
  model: {
    event: valueChangedEvent,
  },
  data() {
    return {
      formValue: null,
    }
  },
  methods: {
    clear() {
      this.setValue(null)
    },

    /**
     * @param {{value: number | string} | undefined | null} option
     */
    optionSelected(option) {
      if (!option) {
        return
      }
      if (option.disabled) {
        return
      }
      if (this.isCheckboxes) {
        const valueToSelect = this.formValue !== option.value ? option.value : null
        this.setValue(valueToSelect)
      } else if (this.formValue !== option.value)
        // radio buttons should only change if the value is different
        this.setValue(option.value)
    },

    selectFirstOption() {
      const firstValue = this.visibleOptions?.[0] ? this.visibleOptions[0].value : null

      if (firstValue) {
        this.setValue(firstValue)
      }
    },

    handlePreselection() {
      const option = this.visibleOptions.find(attr => attr.isPreSelected) ||
        (this.autoselectFirst && this.visibleOptions[0]) || null;
      const selectedValue = option?.value ?? null

      if (!selectedValue)
        return

      this.setValue(selectedValue)
    },

    valueChanged(event) {
      const checked = event.target.checked

      if (checked) {
        this.$emit(valueChangedEvent, event.target.value)
      } else if (this.isCheckboxes) {
        this.clear()
      }
    },

    /**
     * @param {number | string | null} value
     */
    setValue(value) {
      // Avoid emitting the event if the value is the same
      if (this.formValue === value)
        return
      this.formValue = value
      this.$emit(valueChangedEvent, value)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue === oldValue)
          return

        this.formValue = newValue
      },
    },

    visibleOptions: {
      immediate: true,
      handler(newVisibleOptions, oldVisibleOptions) {
        if (_.isEqual(newVisibleOptions, oldVisibleOptions))
          return
        // if options change, reselect or clear
        if (this.shouldPreselect) {
          this.handlePreselection()
        }
        else if (this.formValue != null) {
          const isValueInOptions = !!newVisibleOptions
            && newVisibleOptions.some(attr => attr.value === this.formValue)
          if (!isValueInOptions)
            this.clear()
        }
      },
    },

    isSelectedValueHidden: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // The  selected value is hidden
          this.clear()
        }
      },
    },
  },
  computed: {
    controlId() {
      return this.idPrefix + this.id
    },

    isSelectedValueHidden() {
      if (!this.options) return false
      const formValue = this.formValue
      const selValue = this.options.find((o) => {
        return formValue && formValue == o.value
      })
      return selValue && selValue.hidden ? selValue.hidden : false
    },

    userSignedIn() {
      return this.$store.getters['global/user'] ? this.$store.getters['global/user'].SignedIn : false
    },

    visibleOptions() {
      if (!this.options) return []
      return this.options.filter((option) => (option.hidden ? !option.hidden : true))
    },

    shouldPreselect() {
      return this.autoselectFirst || this.visibleOptions.some(attr => attr.isPreSelected)
    }
  },
  props: {
    autoselectFirst: {
      type: Boolean,
      default: false,
    },

    id: {
      default: '0',
    },

    idPrefix: {
      default: 'product_attribute_',
    },

    isConditional: {
      type: Boolean,
      default: false,
    },

    isCheckboxes: {
      type: Boolean,
      default: false,
    },

    label: {
      default: null,
    },

    options: {
      type: Array,
      required: false,
    },

    value: {
      required: true,
    },
  },
}
</script>